// src/context/AppContext.js
import { shouldNotifyModuleDisabled } from '../utils/moduleDisabled.js';
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// --- API Service Abstraction ---
// Use Flask backend URL during development, empty for production builds
const API_BASE_URL = process.env.REACT_APP_API_URL ?? (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');
const api = axios.create({
    baseURL: `${API_BASE_URL}/api`, // API endpoints are under /api
});

api.interceptors.request.use(config => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// --- FIX: Add a response interceptor for better error logging ---
// Enhanced response interceptor with automatic logout on 401
api.interceptors.response.use(
    response => response,
    error => {
        console.error("API Error:", error.response || error.message);
        const status = error.response?.status;
        const url = error.config?.url || '';

        // Auto-logout on 401 (expired token) — but NOT for a failed login attempt,
        // which legitimately returns 401 and should surface an error, not reload.
        if (status === 401 && !url.includes('/login')) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.reload();
        }

        // Plan limit hit -> prompt to upgrade (skip billing calls themselves).
        if (status === 402 && !url.includes('/billing')) {
            window.dispatchEvent(new CustomEvent('sb:upgrade-required', {
                detail: { message: error.response?.data?.msg || error.response?.data?.message || 'Upgrade required to continue.' }
            }));
        }

        // Feature module not enabled for this tenant (modules.py).
        // Only for user-initiated writes: a GET is a passive page-load fetch and
        // the view already hides itself when its module is off.
        if (shouldNotifyModuleDisabled(error)) {
            window.dispatchEvent(new CustomEvent('sb:module-disabled', {
                detail: { module: error.response.data.module }
            }));
        }

        // On-prem: license expired -> server is view-only for writes.
        if (status === 403 && error.response?.data?.license_readonly) {
            window.dispatchEvent(new CustomEvent('sb:license-readonly', {
                detail: { reason: error.response.data.reason }
            }));
        }

        // On-prem: first-run setup not finished -> back to the setup wizard.
        if (status === 409 && error.response?.data?.setup_required) {
            // Drop any stale session first so the wizard (not the login loop) shows.
            try {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
            } catch { /* storage unavailable */ }
            window.dispatchEvent(new CustomEvent('sb:setup-required'));
            if (window.location.pathname !== '/') {
                window.location.assign('/');
            }
        }

        return Promise.reject(error);
    }
);


// --- Prevent a double-click (or any re-entrant call) from firing the same
// mutating request twice. Every apiService method is wrapped below so that if
// it's called again with identical arguments while the first call is still
// in flight, the caller gets back the SAME promise instead of a second HTTP
// request -- e.g. clicking "Mark Paid" twice quickly only ever hits the API
// once. This is a network-layer safety net that covers every button in the
// app without each component needing its own guard; UI-level disabling
// (better visual feedback) is layered on top for specific hot-path buttons.
function hasNonSerializableArg(args) {
    return args.some(a =>
        (typeof FormData !== 'undefined' && a instanceof FormData) ||
        (typeof File !== 'undefined' && a instanceof File) ||
        (typeof Blob !== 'undefined' && a instanceof Blob)
    );
}

function dedupeInFlight(fn) {
    const inFlight = new Map();
    return (...args) => {
        // Can't safely build a cache key for these (e.g. multipart uploads) --
        // just call through rather than risk an incorrect match.
        if (hasNonSerializableArg(args)) {
            return fn(...args);
        }
        const key = JSON.stringify(args);
        if (inFlight.has(key)) {
            return inFlight.get(key);
        }
        const promise = Promise.resolve(fn(...args)).finally(() => {
            inFlight.delete(key);
        });
        inFlight.set(key, promise);
        return promise;
    };
}

// Define API functions
const rawApiService = {
    api: api, // Export raw axios instance for generic requests
    login: (credentials) => api.post('/login', credentials),
    register: (credentials) => api.post('/register', credentials),
    verifyEmail: (token) => api.post('/verify-email', { token }),
    forgotPassword: (email) => api.post('/forgot-password', { email }),
    resetPassword: (token, new_password) => api.post('/reset-password', { token, new_password }),
    // Billing
    tenantMe: () => api.get('/tenant/me'),
    listPlans: () => api.get('/plans'),
    billingConfig: () => api.get('/billing/config'),
    billingCheckout: (plan) => api.post('/billing/checkout', { plan }),
    billingPortal: () => api.post('/billing/portal'),
    billingContact: (payload) => api.post('/billing/contact', payload),
    billingWhishCheckout: (cycle) => api.post('/billing/whish/checkout', { cycle }),
    // Platform super-admin
    adminTenants: () => api.get('/admin/tenants'),
    adminSuspendTenant: (id) => api.post(`/admin/tenants/${id}/suspend`),
    adminReactivateTenant: (id) => api.post(`/admin/tenants/${id}/reactivate`),
    adminDeleteTenant: (id) => api.delete(`/admin/tenants/${id}`),
    adminSetPlan: (id, plan, extra = {}) => api.post(`/admin/tenants/${id}/set-plan`, { plan, ...extra }),
    adminLicenses: () => api.get('/admin/licenses'),
    adminCreateLicense: (body) => api.post('/admin/licenses', body),
    adminUpdateLicense: (id, body) => api.patch(`/admin/licenses/${id}`, body),
    adminRenewLicense: (id, scope, term) => api.post(`/admin/licenses/${id}/renew`, term ? { scope, term } : { scope }),
    adminLicenseFile: (id) => api.get(`/admin/licenses/${id}/file`, { responseType: 'text' }),
    adminSetModules: (id, overrides) => api.post(`/admin/tenants/${id}/modules`, { overrides }),
    adminUpgradeRequests: () => api.get('/admin/upgrade-requests'),
    adminBillingPayments: () => api.get('/admin/billing/payments'),

    // User Management API methods
    fetchUsers: () => api.get('/users'),
    createUser: (data) => api.post('/users', data),
    updateUser: (userId, data) => api.put(`/users/${userId}`, data),
    deleteUser: (userId) => api.delete(`/users/${userId}`),

    // Reseller API methods
    fetchResellers: () => api.get('/resellers'),
    fetchCustomerFormOptions: () => api.get('/customer-form-options'),
    addReseller: (data) => api.post('/resellers', data),
    updateReseller: (id, data) => api.put(`/resellers/${id}`, data),
    deleteReseller: (id) => api.delete(`/resellers/${id}`),
    addResellerCredit: (id, data) => api.post(`/resellers/${id}/add_credit`, data),
    applyResellerDiscount: (id, data) => api.post(`/resellers/${id}/apply_discount`, data),
    collectResellerPayment: (id, data) => api.post(`/resellers/${id}/collect_payment`, data),
    getResellerHistory: (id) => api.get(`/resellers/${id}/history`),
    getResellerBalanceLog: (id) => api.get(`/resellers/${id}/balance-log`),

    // Upstream Provider API methods (Concept A -- bridged RADIUS subresellers;
    // see docs/superpowers/specs/2026-08-12-network-enforcement-design.md)
    fetchUpstreamProviders: () => api.get('/upstream-providers'),
    addUpstreamProvider: (data) => api.post('/upstream-providers', data),
    updateUpstreamProvider: (id, data) => api.put(`/upstream-providers/${id}`, data),
    deleteUpstreamProvider: (id) => api.delete(`/upstream-providers/${id}`),
    getUpstreamProviderHistory: (id) => api.get(`/upstream-providers/${id}/history`),
    topupUpstreamProvider: (id, data) => api.post(`/upstream-providers/${id}/topup`, data),
    recordUpstreamRenewalCost: (id, data) => api.post(`/upstream-providers/${id}/renewal-cost`, data),

    // Router/device live actions. The Mikrotik Server model was folded into
    // NetworkDevice -- see
    // docs/superpowers/specs/2026-09-07-mikrotik-device-consolidation-design.md
    testNetworkDeviceConnection: (id) => api.post(`/network-devices/${id}/test-connection`),
    fetchCustomerNetworkStatus: (customerId) => api.post(`/customers/${customerId}/network-status`),
    suspendCustomerNetwork: (customerId) => api.post(`/customers/${customerId}/network-suspend`),
    unsuspendCustomerNetwork: (customerId) => api.post(`/customers/${customerId}/network-unsuspend`),

    // Network Device API methods (CRUD + device-health monitoring). These
    // operate on the same NetworkDevice rows as the router/PPPoE actions
    // just above -- the two models were consolidated, see
    // docs/superpowers/specs/2026-09-07-mikrotik-device-consolidation-design.md
    // -- and docs/superpowers/specs/2026-09-01-network-device-health-monitoring-design.md
    // for the health-monitoring side itself.
    fetchNetworkDevices: () => api.get('/network-devices'),
    addNetworkDevice: (data) => api.post('/network-devices', data),
    updateNetworkDevice: (id, data) => api.put(`/network-devices/${id}`, data),
    deleteNetworkDevice: (id) => api.delete(`/network-devices/${id}`),
    checkNetworkDeviceNow: (id) => api.post(`/network-devices/${id}/check-now`),
    setNetworkDeviceInterfaceLabel: (id, data) => api.patch(`/network-devices/${id}/interface-labels`, data),

    // Network Tree (CCR -> OLT -> ONU -> customer) -- see
    // docs/superpowers/specs/2026-09-01-network-topology-tree-design.md
    fetchNetworkTree: () => api.get('/network-tree'),
    refreshOltOnus: (id) => api.post(`/network-tree/olt/${id}/refresh`),
    fetchOnuLabelMatches: (id) => api.get(`/network-tree/olt/${id}/label-matches`),
    applyOnuLabelMatches: (id, links) => api.post(`/network-tree/olt/${id}/label-matches/apply`, { links }),
    fetchNetworkJob: (jobId) => api.get(`/network-jobs/${jobId}`),
    fetchNetworkAgents: () => api.get('/network-agents'),
    createNetworkAgent: (data) => api.post('/network-agents', data),
    regenerateNetworkAgentToken: (id) => api.post(`/network-agents/${id}/regenerate-token`),
    fetchOnuLabelMatchesForJob: (id, jobId) => api.get(`/network-tree/olt/${id}/label-matches?job_id=${jobId}`),
    locateCustomers: (id) => api.post(`/network-tree/olt/${id}/locate-customers`),
    applyCustomerLocations: (id, jobId) =>
        api.post(`/network-tree/olt/${id}/locate-customers/apply`, { job_id: jobId }),

    // Geographic Fiber Map
    fetchNetworkMapOlts: () => api.get('/network-map/olts'),

    // Customer <-> Upstream Portal read-only status sync (staff-triggered, see spec)
    syncCustomerUpstreamStatus: (customerId) => api.post(`/customers/${customerId}/upstream-status-sync`),

    // Supplier API methods
    fetchSuppliers: () => api.get('/suppliers'),
    addSupplier: (data) => api.post('/suppliers', data),
    updateSupplier: (id, data) => api.put(`/suppliers/${id}`, data),
    deleteSupplier: (id) => api.delete(`/suppliers/${id}`),
    fetchSupplierPayments: (id) => api.get(`/suppliers/${id}/payments`),
    recordSupplierPayment: (id, data) => api.post(`/suppliers/${id}/payments`, data),
    fetchSupplierHistory: (id) => api.get(`/suppliers/${id}/history`),
    fixSupplierBalance: (id, data) => api.put(`/suppliers/${id}/fix-balance`, data),
    getSupplierBalanceLog: (id) => api.get(`/suppliers/${id}/balance-log`),
    updateSupplierPayment: (supplierId, paymentId, data) => api.put(`/suppliers/${supplierId}/payments/${paymentId}`, data),
    deleteSupplierPayment: (supplierId, paymentId) => api.delete(`/suppliers/${supplierId}/payments/${paymentId}`),

    // Employee / Payroll API methods
    fetchEmployees: () => api.get('/employees'),
    addEmployee: (data) => api.post('/employees', data),
    updateEmployee: (id, data) => api.put(`/employees/${id}`, data),
    deleteEmployee: (id) => api.delete(`/employees/${id}`),
    fetchEmployeeCharges: (id) => api.get(`/employees/${id}/charges`),
    addEmployeeCharge: (id, data) => api.post(`/employees/${id}/charges`, data),
    updateEmployeeCharge: (id, chargeId, data) => api.put(`/employees/${id}/charges/${chargeId}`, data),
    deleteEmployeeCharge: (id, chargeId) => api.delete(`/employees/${id}/charges/${chargeId}`),
    fetchEmployeePayments: (id) => api.get(`/employees/${id}/payments`),
    recordEmployeePayment: (id, data) => api.post(`/employees/${id}/payments`, data),
    fetchEmployeeHistory: (id) => api.get(`/employees/${id}/history`),
    fixEmployeeBalance: (id, data) => api.put(`/employees/${id}/fix-balance`, data),


    fetchCustomers: async (page = 1, perPage = 999, searchQuery = '', sort_by = 'expiry_date', reseller_id = '', status = 'all', sortDesc = true, expiryDay = '') => {
        const response = await api.get(`/customers`, { params: { page: page, per_page: perPage, search: searchQuery, sort_by: sort_by, reseller_id: reseller_id, status: status, sort_desc: sortDesc, expiry_day: expiryDay } });
        return response.data; // Returns the data object directly
    },
    addCustomer: (customerData) => api.post(`/customers`, customerData),
    // Customer import wizard (docs/superpowers/specs/2026-09-30-customer-import-wizard-design.md).
    downloadImportTemplate: () => api.get('/customers/import/template', { responseType: 'blob' }),
    validateCustomerImport: (file) => {
        const fd = new FormData();
        fd.append('file', file);
        return api.post('/customers/import/validate', fd);
    },
    commitCustomerImport: (file, newPlans) => {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('new_plans', JSON.stringify(newPlans || []));
        return api.post('/customers/import/commit', fd);
    },
    updateCustomer: (customerId, customerData) => api.put(`/customers/${customerId}`, customerData),
    // --- FIX: Ensure all API calls consistently return response.data ---
    fetchSubscriptionPlans: async () => {
        const response = await api.get(`/subscription_plans`);
        return response.data;
    },
    addSubscriptionPlan: (planData) => api.post(`/subscription_plans`, planData),
    updateSubscriptionPlan: (planId, planData) => api.put(`/subscription_plans/${planId}`, planData),
    deleteSubscriptionPlan: (planId) => api.delete(`/subscription_plans/${planId}`),
    fetchPayments: (customerId, status, startDate, endDate, searchQuery, collectedBy, collectedDate, sort_by = 'billed_date', sort_desc = 'true', paidDateStart, paidDateEnd) => api.get(`/payments`, { params: { customer_id: customerId, status: status, start_date: startDate, end_date: endDate, search_query: searchQuery, collected_by: collectedBy, collected_date: collectedDate, sort_by: sort_by, sort_desc: sort_desc, paid_date_start: paidDateStart, paid_date_end: paidDateEnd } }),
    deletePayment: (paymentId) => api.delete(`/payments/${paymentId}`),
    markPaymentAsPaid: (paymentId, data = {}) => api.put(`/payments/${paymentId}/mark_paid`, data),
    // One amount across a customer's bills (oldest first) -- action 'pay' or 'collect'.
    receiveCustomerPayment: (customerId, data) => api.post(`/customers/${customerId}/receive-payment`, data),
    confirmCollectedPayments: (customerId) => api.post(`/customers/${customerId}/confirm-collected`),
    getCustomerBalanceLog: (customerId) => api.get(`/customers/${customerId}/balance-log`),
    getCreditReview: () => api.get('/customers/credit-review'),
    resolveCreditReview: (customerId, action) => api.post(`/customers/${customerId}/credit-review`, { action }),
    markPaymentGratis: (paymentId, note) => api.put(`/payments/${paymentId}/mark_gratis`, { note }),
    revertPayment: (paymentId, reason) => api.put(`/payments/${paymentId}/revert`, { reason }),
    refundPayment: (paymentId, { amount, reason, paid_via }) => api.post(`/payments/${paymentId}/refund`, { amount, reason, paid_via }),
    setPaymentMethod: (paymentId, method, reference) => api.put(`/payments/${paymentId}/method`, { method, reference }),
    cancelSubscription: (customerId) => api.put(`/customers/${customerId}/cancel_subscription`),
    activateSubscription: (customerId) => api.put(`/customers/${customerId}/activate_subscription`),
    deleteCustomer: (customerId) => api.delete(`/customers/${customerId}`),
    // Bulk actions: one HTTP request for a whole selection instead of one per row.
    bulkMarkPaymentsPaid: (paymentIds) => api.post('/payments/bulk_mark_paid', { payment_ids: paymentIds }),
    bulkDeletePayments: (paymentIds) => api.post('/payments/bulk_delete', { payment_ids: paymentIds }),
    bulkRenewSubscriptions: (customerIds) => api.post('/customers/bulk_renew_subscription', { customer_ids: customerIds }),
    bulkCancelSubscriptions: (customerIds) => api.post('/customers/bulk_cancel_subscription', { customer_ids: customerIds }),
    bulkDeleteCustomers: (customerIds) => api.post('/customers/bulk_delete', { customer_ids: customerIds }),
    fetchBusinessSettings: () => api.get('/business-settings'),
    saveBusinessSettings: (formData) => api.post('/business-settings', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    fetchWhatsAppSettings: () => api.get('/whatsapp-settings'),
    // Secrets-free subset (enabled/mode/deep-link texts) readable by cashier/collector.
    fetchWhatsAppDeeplinkSettings: () => api.get('/whatsapp-settings/deeplink'),
    saveWhatsAppSettings: (data) => api.post('/whatsapp-settings', data),
    subscribeWaba: () => api.post('/whatsapp/subscribe-waba'),
    // Automatic WhatsApp connection (specs/001-whatsapp-auto-connect/contracts/http-api.md)
    getWaConnectConfig: () => api.get('/whatsapp/connect/config'),
    getWaConnectStatus: () => api.get('/whatsapp/connect/status'),
    completeWaConnect: (payload) => api.post('/whatsapp/connect/complete', payload),
    refreshWaConnect: () => api.post('/whatsapp/connect/refresh'),
    sendWaTestMessage: (payload) => api.post('/whatsapp/connect/test-message', payload),
    disconnectWa: (payload = {}) => api.post('/whatsapp/connect/disconnect', { confirm: true, ...payload }),
    getStandardTemplates: () => api.get('/whatsapp/standard-templates'),
    submitStandardTemplates: (payload) => api.post('/whatsapp/standard-templates/submit', payload),
    tenantWhishSettings: () => api.get('/tenant-whish-settings'),
    saveTenantWhishSettings: (payload) => api.post('/tenant-whish-settings', payload),
    getPublicPayLink: () => api.get('/tenant/whish/public-pay-link'),
    regeneratePublicPayLink: () => api.post('/tenant/whish/public-pay-link/regenerate'),
    resendWhishPaymentLink: (customerId, paymentId) => api.post(`/customers/${customerId}/payments/${paymentId}/whish-link/resend`),
    emailWhishPaymentLink: (customerId, paymentId, email, payUrl) => api.post(`/customers/${customerId}/payments/${paymentId}/whish-link/email`, { email, pay_url: payUrl }),
    sendWhatsappReminder: (customerId, templateType = 'payment_reminder') => api.post(`/customers/${customerId}/send-whatsapp-reminder`, { template_type: templateType }),
    fetchReceipt: (paymentId) => api.get(`/receipt/${paymentId}`),
    fetchUnpaidReceipt: (customerId) => api.get('/customers/' + customerId + '/unpaid_receipt'),
    addCustomerPayment: (paymentData) => api.post(`/payments`, paymentData),
    renewSubscription: (customerId) => api.post(`/customers/${customerId}/renew_subscription`),
    fetchCustomerBalance: (customerId) => api.get(`/customers/${customerId}/balance`),
    generateFuturePayments: (data) => api.post('/payments/generate_future', data),
    fetchReceiptLogs: (searchQuery, printed_filter = 'false', sort_by = 'billing_date', sort_desc = 'true') => api.get('/receipts/with-current-balance', { params: { search_query: searchQuery, printed: printed_filter, sort_by: sort_by, sort_desc: sort_desc } }),
    generateReceipts: (data) => api.post('/receipts/generate', data),
    logReceiptPrint: (data) => api.post('/receipts/log_print', data),
    deleteReceipt: (receiptId) => api.delete(`/receipts/${receiptId}`),

    // New Expense API methods
    fetchExpenses: (startDate, endDate) => api.get(`/expenses`, { params: { start_date: startDate, end_date: endDate } }),
    addExpense: (expenseData) => api.post(`/expenses`, expenseData),
    updateExpense: (expenseId, expenseData) => api.put(`/expenses/${expenseId}`, expenseData),
    deleteExpense: (expenseId) => api.delete(`/expenses/${expenseId}`),

    fetchExpenseCategories: () => api.get('/expense_categories'),
    addExpenseCategory: (categoryData) => api.post('/expense_categories', categoryData),
    updateExpenseCategory: (categoryId, categoryData) => api.put(`/expense_categories/${categoryId}`, categoryData),
    deleteExpenseCategory: (categoryId) => api.delete(`/expense_categories/${categoryId}`),

    fetchSectors: () => api.get('/sectors'),
    addSector: (sectorData) => api.post('/sectors', sectorData),
    updateSector: (sectorId, sectorData) => api.put(`/sectors/${sectorId}`, sectorData),
    deleteSector: (sectorId) => api.delete(`/sectors/${sectorId}`),

    fetchDashboardMetrics: (startDate, endDate) => api.get('/dashboard', { params: {
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {})
    } }),

    // Reports
    fetchMonthlyRevenue: () => api.get('/reports/monthly-revenue'),
    fetchTotalSales: () => api.get('/reports/total-sales'),
    fetchUnpaidPayments: () => api.get('/reports/unpaid-payments'),
    fetchOverduePayments: () => api.get('/reports/overdue'),
    fetchCustomerNumbers: () => api.get('/reports/customer-numbers'),
    fetchExpensesTotal: () => api.get('/reports/expenses-total'),
    fetchActiveSubscriptionsByPlan: () => api.get('/reports/active-subscriptions-by-plan'),
    fetchFinancialReport: (startDate, endDate) => api.get(`/reports/financial`, { params: { start_date: startDate, end_date: endDate } }),
    fetchCollectorProgressReport: (startDate, endDate) => api.get('/reports/collector-progress', { params: { start_date: startDate, end_date: endDate } }),

    // Service Management
    fetchServiceStatuses: () => api.get('/service-statuses'), // Note: a new endpoint will be added to app.py
    fetchSupportTickets: () => api.get('/support-tickets'),
    fetchServiceOutages: () => api.get('/service-outages'),
    createSupportTicket: (data) => api.post('/support-tickets', data),
    updateSupportTicket: (ticketId, data) => api.put(`/support-tickets/${ticketId}`, data),
    deleteSupportTicket: (ticketId) => api.delete(`/support-tickets/${ticketId}`),
    bulkDeleteSupportTickets: (ticketIds) => api.post('/support-tickets/bulk_delete', { ticket_ids: ticketIds }),
    createServiceOutage: (data) => api.post('/service-outages', data),
    updateServiceOutage: (outageId, data) => api.put(`/service-outages/${outageId}`, data),
    updateServiceStatusById: (statusId, data) => api.put(`/service-statuses/${statusId}`, data),
    
    // Bulk Messaging
    sendBulkMessage: (payload) => api.post('/messages/bulk_send', payload),
    fetchMetaTemplates: () => api.get('/whatsapp/templates'),
    fetchWhatsAppTemplates: () => api.get('/whatsapp/templates'),
    syncWhatsAppTemplates: () => api.post('/whatsapp/templates/sync'),
    createWhatsAppTemplate: (data) => api.post('/whatsapp/templates', data),
    updateWhatsAppTemplate: (id, data) => api.put(`/whatsapp/templates/${id}`, data),
    deleteWhatsAppTemplate: (id) => api.delete(`/whatsapp/templates/${id}`),
    uploadWhatsAppTemplateSample: (formData) => api.post('/whatsapp/templates/upload-sample', formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }),

    // WhatsApp Inbox (see docs/superpowers/specs/2026-09-23-whatsapp-inbox-design.md)
    fetchInboxSummary: () => api.get('/whatsapp/inbox/summary'),
    fetchInboxConversations: (params) => api.get('/whatsapp/inbox/conversations', { params }),
    fetchInboxMessages: (id, before) => api.get(`/whatsapp/inbox/conversations/${id}/messages`, { params: before ? { before } : {} }),
    markInboxRead: (id) => api.post(`/whatsapp/inbox/conversations/${id}/read`),
    resolveInboxConversation: (id) => api.post(`/whatsapp/inbox/conversations/${id}/resolve`),
    pauseInboxConversation: (id) => api.post(`/whatsapp/inbox/conversations/${id}/pause`),
    sendInboxMessage: (id, payload) => api.post(`/whatsapp/inbox/conversations/${id}/send`, payload),
    sendInboxFile: (id, type, file) => {
        const fd = new FormData();
        fd.append('type', type);
        fd.append('file', file);
        return api.post(`/whatsapp/inbox/conversations/${id}/send`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    fetchInboxMedia: (messageId, variant = 'original') => api.get(`/whatsapp/inbox/media/${messageId}`, { params: { variant }, responseType: 'blob' }),

    // Web push (per-device)
    fetchVapidPublicKey: () => api.get('/vapid-public-key'),
    pushSubscribe: (subscription) => api.post('/push-subscribe', { subscription }),
    fetchPushTopics: (endpoint) => api.get('/push-subscription/topics', { params: { endpoint } }),
    setPushTopics: (endpoint, topics) => api.put('/push-subscription/topics', { endpoint, topics }),
    unsubscribePush: (endpoint) => api.post('/push-unsubscribe', { endpoint }),
    sendTestPush: (endpoint) => api.post('/push-test', { endpoint }),

    // On-prem edition: system info, first-run setup, license
    systemInfo: () => api.get('/system/info'),
    setupStatus: () => api.get('/setup/status'),
    runSetup: (body) => api.post('/setup', body),
    getLicense: () => api.get('/license'),
    activateLicense: (license_key) => api.post('/license', { license_key }),
    uploadLicenseFile: (license_file) => api.post('/license', { license_file }),
    refreshLicense: () => api.post('/license/refresh'),
};

export const apiService = Object.fromEntries(
    Object.entries(rawApiService).map(([name, value]) =>
        // `api` is the raw axios instance -- itself a callable function with
        // .get/.post/etc. attached. Wrapping it in dedupeInFlight would replace
        // it with a plain function missing those methods, so leave it as-is.
        [name, (name !== 'api' && typeof value === 'function') ? dedupeInFlight(value) : value]
    )
);

// --- Context for shared state ---
export const AppContext = createContext();

export const AppContextProvider = ({ children }) => {
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [user, setUser] = useState(() => {
        try {
            const u = localStorage.getItem('user');
            return u && u !== 'undefined' ? JSON.parse(u) : null;
        } catch {
            return null;
        }
    });
    const [isAuthenticated, setIsAuthenticated] = useState(!!token);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (token) {
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user ?? null));
            setIsAuthenticated(true);
        } else {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setIsAuthenticated(false);
        }
    }, [token, user]);

    // Surface plan-limit (402) prompts raised by the axios interceptor.
    useEffect(() => {
        const onUpgrade = (e) => setSnackbar({
            open: true,
            message: `${e.detail?.message || 'Upgrade required.'} Open Billing & Plan to upgrade.`,
            severity: 'warning',
        });
        window.addEventListener('sb:upgrade-required', onUpgrade);
        return () => window.removeEventListener('sb:upgrade-required', onUpgrade);
    }, []);

    // Enabled feature modules for this tenant: null while loading, else array.
    // Super-admins have no tenant (call fails) -> [].
    const [modules, setModules] = useState(null);
    const refreshModules = React.useCallback(() => {
        if (!token) { setModules(null); return Promise.resolve(); }
        // Super-admins have no tenant: /tenant/me would 401, and the 401
        // interceptor above would log them straight back out.
        if ((user?.role || '').split(',').includes('superadmin')) { setModules([]); return Promise.resolve(); }
        return apiService.tenantMe()
            .then(r => setModules(r.data?.modules || []))
            .catch(() => setModules([]));
    }, [token, user]);
    useEffect(() => { refreshModules(); }, [refreshModules]);

    useEffect(() => {
        const onModuleDisabled = () => setSnackbar({
            open: true,
            message: "This feature isn't included in your plan.",
            severity: 'warning',
        });
        window.addEventListener('sb:module-disabled', onModuleDisabled);
        return () => window.removeEventListener('sb:module-disabled', onModuleDisabled);
    }, []);

    // Deployment info (no auth). null while loading (or if the request failed,
    // in which case the app behaves as SaaS).
    const [systemInfo, setSystemInfo] = useState(null);
    const [systemInfoLoaded, setSystemInfoLoaded] = useState(false);
    const refreshSystemInfo = React.useCallback(() =>
        apiService.systemInfo()
            .then(r => setSystemInfo(r.data || null))
            .catch(() => setSystemInfo(null))
            .finally(() => setSystemInfoLoaded(true)),
    []);
    useEffect(() => { refreshSystemInfo(); }, [refreshSystemInfo]);
    useEffect(() => { if (token) refreshSystemInfo(); }, [token, refreshSystemInfo]);
    const isOnprem = systemInfo?.deployment_mode === 'onprem';
    const readOnly = isOnprem && systemInfo?.license?.state === 'readonly';

    useEffect(() => {
        const onReadonly = () => {
            setSnackbar({
                open: true,
                message: 'ServiceBills is in view-only mode — enter a license in Settings → License.',
                severity: 'warning',
            });
            refreshSystemInfo();
        };
        window.addEventListener('sb:license-readonly', onReadonly);
        return () => window.removeEventListener('sb:license-readonly', onReadonly);
    }, [refreshSystemInfo]);

    // A stale session hit 409 setup_required: forget it and show the wizard.
    useEffect(() => {
        const onSetup = () => {
            setToken(null);
            setUser(null);
            refreshSystemInfo();
        };
        window.addEventListener('sb:setup-required', onSetup);
        return () => window.removeEventListener('sb:setup-required', onSetup);
    }, [refreshSystemInfo]);

    // While the app stays open in read-only mode, re-check the license every
    // 5 minutes so an expiry (or a renewal) mid-session is reflected.
    useEffect(() => {
        if (!isOnprem) return undefined;
        const id = setInterval(refreshSystemInfo, 5 * 60 * 1000);
        return () => clearInterval(id);
    }, [isOnprem, refreshSystemInfo]);

    const hasModule = (key) => key === 'core' || key === 'office' || (Array.isArray(modules) && modules.includes(key));

    const login = async (credentials) => {
        const response = await apiService.login(credentials);
        setToken(response.data.access_token);
        setUser(response.data.user);
        return response;
    };

    const logout = () => {
        setToken(null);
        setUser(null);
    };

    const value = {
        apiService,
        snackbar,
        setSnackbar,
        token,
        user,
        isAuthenticated,
        modules,
        hasModule,
        refreshModules,
        systemInfo,
        systemInfoLoaded,
        isOnprem,
        readOnly,
        refreshSystemInfo,
        login,
        logout
    };

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
};

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useAppContext must be used within an AppContextProvider');
    }
    return context;
};
